"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { CREATE_UA_BIGQUERY_NAME } from "./constants";
import type { CreateUserAnalyticsBigQueryInput } from "./schema";

const ProjectDataQuery = graphql(`
  query UaBigQueryConfirmProject($id: ID!) {
    node(id: $id) {
      __typename
      ... on UserAnalyticsProject {
        name
        userAnalytics {
          id
          __typename
        }
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function CreateUserAnalyticsBigQueryConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isTarget =
    isToolUIPart(part) && getToolName(part) === CREATE_UA_BIGQUERY_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<CreateUserAnalyticsBigQueryInput>;
  const projectId = input.projectId ?? "";

  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  const projectName =
    data?.node?.__typename === "UserAnalyticsProject"
      ? data.node.name
      : undefined;

  if (!isTarget || !isInputAvailable) return null;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Register User Analytics BigQuery configuration?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>GCP project</Trans>:{" "}
          <span className="font-mono font-medium">{input.projectID}</span>
        </li>
        <li>
          <Trans>Dataset</Trans>:{" "}
          <span className="font-mono font-medium">{input.datasetName}</span>
        </li>
        <li>
          <Trans>Event source</Trans>:{" "}
          <span className="font-medium">
            {input.eventSourceType ?? "RAW_EVENTS_TABLES"}
          </span>
        </li>
        {input.eventSourceType === "DERIVED_VIEW" ? (
          <li>
            <Trans>View name</Trans>:{" "}
            <span className="font-mono font-medium">
              {input.eventSourceViewName}
            </span>
          </li>
        ) : null}
        {input.selectedEventParamKeys ? (
          <li>
            <Trans>Event parameter keys</Trans>:{" "}
            <span className="font-mono text-xs">
              {input.selectedEventParamKeys.length > 0
                ? input.selectedEventParamKeys.join(", ")
                : "—"}
            </span>
          </li>
        ) : null}
        {input.organizationId ? (
          <li>
            <Trans>Organization ID</Trans>:{" "}
            <span className="break-all font-mono text-xs">
              {input.organizationId}
            </span>
          </li>
        ) : null}
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName ?? "Unknown"}</span>
            )}
          </li>
        ) : null}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Register</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
